<template>
  <generic-modal :title="$t('payment')" ref="generic_modal" size="fullscreen">
    <form>
      <div class="columns">
        <div class="column is-half">
          <!-- Payment Basic Information -->
          <div class="box">
            <h5 class="title is-5">{{ $t("payment information") }}</h5>

            <div class="field">
              <generic-dropdown
                :title="$t('select loan')"
                :placeholder="$t('select one')"
                :is_multiple="false"
                url="/loan/loan/catalog"
                name_field="loan_number"
                value_field="loan_id"
                @selected="loan_changed"
                ref="loan_dropdown"
              />
            </div>

            <!-- Información del préstamo seleccionado -->
            <div v-if="selected_loan" class="box">
              <h6 class="title is-6 has-text-info">
                <span class="icon">
                  <i class="fa fa-info-circle"></i>
                </span>
                {{ $t("selected loan information") }}
              </h6>

              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <div>
                      <p class="heading">{{ $t("loan number") }}</p>
                      <p class="title is-6">{{ selected_loan.loan_number }}</p>
                    </div>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <div>
                      <p class="heading">{{ $t("outstanding balance") }}</p>
                      <p class="title is-6 has-text-danger">
                        {{ format_currency(selected_loan.outstanding_balance) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="level" v-if="selected_loan.customer">
                <div class="level-left">
                  <div class="level-item">
                    <div>
                      <p class="heading">{{ $t("customer") }}</p>
                      <p class="subtitle is-7">
                        <span class="icon">
                          <i class="fa fa-user"></i>
                        </span>
                        {{ selected_loan.customer.first_name }}
                        {{ selected_loan.customer.last_name }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <div>
                      <p class="heading">{{ $t("loan type") }}</p>
                      <p class="subtitle is-7">
                        <span class="icon">
                          <i class="fa fa-tag"></i>
                        </span>
                        {{ selected_loan.loan_type.name }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Información del próximo pago -->
              <div v-if="selected_loan.next_payment" class="box">
                <h6 class="subtitle is-6 has-text-warning">
                  <span class="icon">
                    <i class="fa fa-calendar-check-o"></i>
                  </span>
                  {{ $t("next payment due") }}
                </h6>

                <div class="columns is-mobile">
                  <div class="column is-3">
                    <div class="field">
                      <p class="heading">{{ $t("installment") }}</p>
                      <span class="tag is-warning is-rounded">{{
                        selected_loan.next_payment.installment_number
                      }}</span>
                    </div>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <p class="heading">{{ $t("due date") }}</p>
                      <p class="has-text-weight-semibold">
                        {{ format_date(selected_loan.next_payment.due_date) }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <p class="heading">{{ $t("principal") }}</p>
                      <p class="has-text-weight-semibold">
                        {{
                          format_currency(
                            selected_loan.next_payment.principal_amount
                          )
                        }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <p class="heading">{{ $t("interest") }}</p>
                      <p class="has-text-weight-semibold">
                        {{
                          format_currency(
                            selected_loan.next_payment.interest_amount
                          )
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="level">
                  <div class="level-left">
                    <div class="level-item">
                      <div>
                        <p class="heading">{{ $t("total amount") }}</p>
                        <p class="title is-5 has-text-primary">
                          {{
                            format_currency(
                              selected_loan.next_payment.total_amount
                            )
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <!-- <div class="level-right">
                    <div class="level-item">
                      <button
                        class="button is-warning is-small"
                        @click="populate_payment_fields"
                        type="button"
                      >
                        <span class="icon">
                          <i class="fa fa-magic"></i>
                        </span>
                        <span>{{ $t("auto fill payment") }}</span>
                      </button>
                    </div>
                  </div> -->
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label is-large"
                >{{ $t("amount") }}
                <span class="has-text-danger">*</span></label
              >
              <div class="control">
                <input
                  type="number"
                  class="input is-large"
                  v-model="meta.amount"
                  step="0.01"
                  :class="{ 'is-danger': !meta.amount }"
                />
              </div>
            </div>
          </div>

          <!-- Payment Breakdown -->
          <div class="box">
            <h5 class="title is-5">{{ $t("payment breakdown") }}</h5>

            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("principal amount") }}</label>
                  <div class="control">
                    <input
                      type="number"
                      class="input"
                      v-model="meta.principal_amount"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("interest amount") }}</label>
                  <div class="control">
                    <input
                      type="number"
                      class="input"
                      v-model="meta.interest_amount"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("late fee amount") }}</label>
                  <div class="control">
                    <input
                      type="number"
                      class="input"
                      v-model="meta.late_fee_amount"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("other fees") }}</label>
                  <div class="control">
                    <input
                      type="number"
                      class="input"
                      v-model="meta.other_fees"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column is-half">
          <!-- Payment Method and Status -->
          <div class="box">
            <h5 class="title is-5">{{ $t("payment method and status") }}</h5>

            <div class="field">
              <label class="label"
                >{{ $t("payment method") }}
                <span class="has-text-danger">*</span></label
              >
              <div class="control">
                <div
                  class="select is-fullwidth"
                  :class="{ 'is-danger': !meta.payment_method }"
                >
                  <select v-model="meta.payment_method">
                    <option value="">{{ $t("select payment method") }}</option>
                    <option value="zelle">{{ $t("zelle") }}</option>
                    <option value="cod">{{ $t("cod") }}</option>
                    <option value="paq">{{ $t("paq") }}</option>
                    <option value="transference">
                      {{ $t("transference") }}
                    </option>
                    <option value="pos">{{ $t("pos") }}</option>
                    <option value="wallet">{{ $t("wallet") }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label"
                >{{ $t("status") }}
                <span class="has-text-danger">*</span></label
              >
              <div class="control">
                <div
                  class="select is-fullwidth"
                  :class="{ 'is-danger': !meta.status }"
                >
                  <select v-model="meta.status">
                    <option value="">{{ $t("select status") }}</option>
                    <option value="pending">{{ $t("pending") }}</option>
                    <option value="approved">{{ $t("approved") }}</option>
                    <option value="failed">{{ $t("failed") }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">{{ $t("transaction reference") }}</label>
              <div class="control">
                <input
                  type="text"
                  class="input"
                  v-model="meta.transaction_reference"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">{{ $t("payment date") }}</label>
              <div class="control">
                <input type="date" class="input" v-model="meta.payment_date" />
              </div>
            </div>
          </div>

          <!-- Additional Information -->
          <div class="box">
            <h5 class="title is-5">{{ $t("additional information") }}</h5>

            <div class="field">
              <label class="label">{{ $t("receipt number") }}</label>
              <div class="control">
                <input
                  type="text"
                  class="input"
                  v-model="meta.receipt_number"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">{{ $t("notes") }}</label>
              <div class="control">
                <textarea
                  class="textarea"
                  v-model="meta.notes"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>

    <div class="notification is-danger" v-if="error" v-html="error"></div>

    <template v-slot:footer>
      <button
        class="button is-primary is-rounded"
        @click="save"
        :disabled="is_form_invalid"
      >
        <span class="icon">
          <i class="fa fa-save"></i>
        </span>
        <span>{{ $t("save") }}</span>
      </button>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon">
          <i class="fa fa-close"></i>
        </span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import moment from "moment";
import {
  generate_error_message_text,
  del,
  post,
  put,
  get,
  format_currency,
  format_date,
} from "@/utils";

export default {
  name: "PaymentModal",
  components: { GenericModal, GenericDropdown },
  data() {
    let v = this;
    return {
      meta: {
        payment_date: new Date().toISOString().split("T")[0],
        status: "pending",
        principal_amount: 0,
        interest_amount: 0,
        late_fee_amount: 0,
        other_fees: 0,
      },
      error: "",
      is_clone: false,
      selected_loan: null,
    };
  },
  computed: {
    is_form_invalid() {
      return (
        !this.meta.loan_id ||
        !this.meta.payment_date ||
        !this.meta.amount ||
        !this.meta.payment_method ||
        !this.meta.status
      );
    },
  },
  methods: {
    format_currency,
    format_date,
    show_modal(uuid = null, clone = false) {
      let v = this;

      v.is_clone = clone;
      v.$refs.generic_modal.show_modal();
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    save() {
      let v = this;
      if (v.is_form_invalid) return;

      let data = { ...v.meta };

      put("/loan/payment", data)
        .then(() => {
          v.$emit("success");
          v.close_modal();
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        });
    },
    set_initial_state() {
      let v = this;
      v.meta = {
        payment_date: new Date().toISOString().split("T")[0],
        status: "pending",
        principal_amount: 0,
        interest_amount: 0,
        late_fee_amount: 0,
        other_fees: 0,
      };
      v.selected_loan = null;
      v.error = "";
      v.payment_uuid = "";
      v.is_clone = false;
      v.$refs.loan_dropdown.clear();
    },
    loan_changed(selected) {
      let v = this;
      if (selected) {
        v.selected_loan = selected;
        v.meta.loan_id = selected.loan_id;
      }
      setTimeout(() => {
        v.populate_payment_fields();
      }, 300);
    },
    populate_payment_fields() {
      let v = this;
      if (v.selected_loan && v.selected_loan.next_payment) {
        const nextPayment = v.selected_loan.next_payment;

        // Poblar campos con la información del próximo pago
        v.meta.installment_number = nextPayment.installment_number;
        v.meta.principal_amount = nextPayment.principal_amount;
        v.meta.interest_amount = nextPayment.interest_amount;

        // Comparar la fecha de pago con la fecha de vencimiento y calcular el cargo por mora
        let payment_date = moment(v.meta.payment_date);
        let grace_period_days = v.selected_loan.grace_period_days || 0;
        let limit_date = moment(nextPayment.due_date).add(
          grace_period_days,
          "days"
        );
        let late_fee_percentage = v.selected_loan.late_fees || 0;
        let late_fee_amount = 0;
        if (payment_date > limit_date) {
          let days_late = moment(payment_date).diff(limit_date, "days");
          late_fee_amount =
            (Number(late_fee_percentage) *
              Math.max(0, days_late) *
              Number(nextPayment.principal_amount)) /
            100;
          v.meta.late_fee_amount = late_fee_amount.toFixed(2);
        } else {
          v.meta.late_fee_amount = 0; // No hay cargo por mora
        }
      }
    },
    calculate_total() {
      let v = this;
      let r =
        Number(v.meta.principal_amount || 0) +
        Number(v.meta.interest_amount || 0) +
        Number(v.meta.late_fee_amount || 0) +
        Number(v.meta.other_fees || 0);
      v.meta.amount = r.toFixed(2);
    },
  },
  watch: {
    "meta.principal_amount": function () {
      this.calculate_total();
    },
    "meta.interest_amount": function () {
      this.calculate_total();
    },
    "meta.late_fee_amount": function () {
      this.calculate_total();
    },
    "meta.other_fees": function () {
      this.calculate_total();
    },
    "meta.payment_date": function () {
      this.populate_payment_fields();
    },
  },
};
</script>
